/* ==========================================================================
   The dashboard

   Not analytics. Six or seven questions somebody running a young marketplace
   actually asks, answered plainly:

     Are both sides growing, or only one?
     Is work getting answered, or piling up unanswered?
     Is anyone coming back, or is it all one-off?
     What are people saying, and what are they saying about each other?
     What are they using — a laptop, a phone browser, or the installed app?

   Every count carries the one before it, because a number on its own is not
   readable. "4 homes" means nothing. "4 homes, 1 last week" means something.
   ========================================================================== */

import { useEffect, useState } from 'react';
import { Card, Empty, SectionHead, Seg } from '../../components/ui';
import { ApiError, api, type VisitorReport } from '../../api';
import { nav, toast } from '../../store';
import { ago } from '../../lib/time';

type Span = 'today' | 'week' | 'month' | 'quarter';

interface Pair { now: number; before: number }

interface Stats {
  span: string;
  from: string;
  people: {
    homes: Pair; cleaners: Pair; totalHomes: number; totalCleaners: number;
  };
  requests: { posted: Pair; filled: Pair; openNow: number; unanswered: number };
  bookings: {
    made: Pair; completed: Pair; recurring: number; oneOff: number;
    unpaid: number; activeSeries: number;
  };
  devices: { device: string; people: number; hits: number }[];
  features: { feature: string; people: number; uses: number; roles: Record<string, number> }[];
  reports: {
    cleaner: string; by: string; date: string; reasons: string[]; detail: string; at: string;
  }[];
  feedback: {
    name: string; role: string; topic: string; text: string; screen: string; at: string;
  }[];
  billing: {
    paid: number; paying: number; onTrial: number; browsing: number;
    endingSoon: number; cancelled: number;
  };
  promos: {
    minted: number; redeemed: number; unused: number; monthsGiven: number;
    recent: { code: string; months: number; day: string; name: string;
      role: string; note: string }[];
  };
  referrals: {
    signups: number; sharers: number; monthsEarned: number; atCap: number;
    top: { name: string; role: string; joined: number; months: number }[];
  };
  deletions: {
    total: number; homes: number; cleaners: number; lastWeek: number;
    reasons: { reason: string; n: number }[];
    recent: { role: string; town: string; joined: string; left: string; stayed: string;
      reason: string | null; note: string | null }[];
  };
}

/* What each leaving reason reads as here. */
const LEFT_BECAUSE: Record<string, string> = {
  'nobody-near': 'nobody near them', 'too-expensive': 'too expensive', 'just-looking': 'just looking',
  'found-elsewhere': 'found another way', 'not-working': 'something didn’t work', other: 'something else',
  unsaid: 'didn’t say',
};

const SPANS: { id: Span; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'quarter', label: 'Quarter' },
];

const DEVICE_NAMES: Record<string, string> = {
  'mobile-web': 'Phone browser',
  'desktop-web': 'Desktop',
  'installed-app': 'Installed app',
};

interface Code { phone: string; code: string; expiresAt: string }

export function Admin() {
  const [span, setSpan] = useState<Span>('week');
  const [stats, setStats] = useState<Stats | null>(null);
  const [failed, setFailed] = useState('');
  const [codes, setCodes] = useState<Code[]>([]);

  /* Codes waiting to be read out. Two people need this: somebody Twilio could
     not reach, and Oster testing on a made-up number, which never had a text
     sent to it in the first place. Polled rather than fetched once, because
     the whole use of it is watching one turn up while you are signing in on
     another device. */
  useEffect(() => {
    let alive = true;
    const load = () => api.pendingCodes()
      .then((r) => { if (alive) setCodes(r.codes); })
      .catch(() => { /* the dashboard is still worth showing without it */ });
    void load();
    const timer = setInterval(load, 5000);
    return () => { alive = false; clearInterval(timer); };
  }, []);

  /* Bumped by the mint and comp controls so their effect shows immediately.
     Deliberately does NOT blank stats first the way a span change does --
     re-reading after minting should update the numbers in place, not throw
     the page away and make somebody wait for it to come back. */
  const [refresh, setRefresh] = useState(0);
  const reload = () => setRefresh((n) => n + 1);

  useEffect(() => {
    let alive = true;
    if (refresh === 0) setStats(null);
    api.stats(span)
      .then((r) => { if (alive) setStats(r as Stats); })
      .catch((e) => { if (alive) setFailed(e?.message ?? 'Could not load that.'); });
    return () => { alive = false; };
  }, [span, refresh]);

  /* A span change is a different question, so that one does blank it. */
  useEffect(() => { setRefresh(0); }, [span]);

  if (failed) {
    return <div className="screen-pad"><Empty big="Not available" sub={failed} /></div>;
  }

  return (
    <div className="screen-pad">
      {/* First, and not behind the time-span selector, because it is not a
          question about a window: it is "is anything on fire right now". */}
      <HealthCard />

      <Seg options={SPANS} value={span} onChange={(v) => setSpan(v)} />

      <Card className="tint">
        <SectionHead title="The data itself" />
        <span className="hint">
          Every table, and a box to write SQL in. Live, and read-only: the
          database is opened read-only, so a write is refused rather than
          checked for.
        </span>
        <button type="button" className="btn" onClick={() => nav('sql')}>Open SQL</button>
      </Card>

      <DeviceLayout />

      {codes.length > 0 && (
        <Card className="warm">
          <SectionHead title="Sign-in codes waiting" />
          <span className="hint">
            A code nobody could be texted — a made-up 555-01xx number, or somebody
            Twilio could not reach. Read it out; it expires on its own.
          </span>
          {codes.map((c) => (
            <div className="row between codeline" key={c.phone}>
              <b>{c.phone}</b>
              <span className="codeval">{c.code}</span>
            </div>
          ))}
        </Card>
      )}

      <WebsiteVisitors />

      {!stats ? (
        <Card><span className="sub">Counting…</span></Card>
      ) : (
        <>
          <Card className="tint">
            <SectionHead title="Both sides" />
            <div className="stat-row">
              <Stat
                label="Homes joined"
                pair={stats.people.homes}
                foot={stats.people.totalHomes + ' in all'}
              />
              <Stat
                label="Cleaners joined"
                pair={stats.people.cleaners}
                foot={stats.people.totalCleaners + ' in all'}
              />
            </div>
            <span className="hint">
              {stats.people.totalCleaners < stats.people.totalHomes
                ? 'More homes than cleaners. Homes arrive, find nobody, and leave — this is the number to fix first.'
                : 'Cleaners at least keep pace with homes, which is the right way round.'}
            </span>
          </Card>

          <Card>
            <SectionHead title="Work" />
            <div className="stat-row">
              <Stat label="Requests posted" pair={stats.requests.posted} />
              <Stat label="Found somebody" pair={stats.requests.filled} />
            </div>
            <div className="row between">
              <span className="sub">Open right now</span>
              <b>{stats.requests.openNow}</b>
            </div>
            <div className="row between">
              <span className="sub">
                Open with <b>nobody applied</b>
              </span>
              <b style={{ color: stats.requests.unanswered ? 'var(--bergamot-deep)' : 'inherit' }}>
                {stats.requests.unanswered}
              </b>
            </div>
            {stats.requests.unanswered > 0 && (
              <span className="hint">
                A home that posts and hears nothing does not post twice. This is the most
                expensive number on the page.
              </span>
            )}
          </Card>

          <Card>
            <SectionHead title="Visits" />
            <div className="stat-row">
              <Stat label="Booked" pair={stats.bookings.made} />
              <Stat label="Finished" pair={stats.bookings.completed} />
            </div>
            {/* Side by side, never added together: they are different
                businesses, and the sum of them says nothing about either. */}
            <div className="row between">
              <span className="sub">Every week</span>
              <b>{stats.bookings.recurring}</b>
            </div>
            <div className="row between">
              <span className="sub">One time</span>
              <b>{stats.bookings.oneOff}</b>
            </div>
            <div className="row between">
              <span className="sub">Running arrangements</span>
              <b>{stats.bookings.activeSeries}</b>
            </div>
            <div className="row between">
              <span className="sub">Finished, not marked paid</span>
              <b>{stats.bookings.unpaid}</b>
            </div>
            <span className="hint">
              Repeat work is the whole business. A marketplace where nobody books the same
              person twice is a lead generator, not a marketplace.
            </span>
          </Card>

          <Card>
            <SectionHead title="What people use" />
            {stats.devices.length === 0 ? (
              <span className="sub">Nothing recorded in this stretch.</span>
            ) : (
              stats.devices.map((d) => (
                <div className="row between" key={d.device}>
                  <span className="sub">{DEVICE_NAMES[d.device] ?? d.device}</span>
                  <b>
                    {d.people} {d.people === 1 ? 'person' : 'people'}
                    <small style={{ color: 'var(--faint)', fontWeight: 600 }}>
                      {' · '}{d.hits} visits
                    </small>
                  </b>
                </div>
              ))
            )}
            <span className="hint">
              This is the honest answer to whether real iOS and Android apps are worth
              building — not a guess about what people prefer.
            </span>
          </Card>

          <Card>
            <SectionHead title="What people actually do" />
            {stats.features.length === 0 ? (
              <span className="sub">Nothing recorded in this stretch.</span>
            ) : (
              <>
                {stats.features.map((f) => (
                  <div className="row between" key={f.feature}>
                    <span className="sub">{f.feature}</span>
                    <b>
                      {f.people} {f.people === 1 ? 'person' : 'people'}
                      <small style={{ color: 'var(--faint)', fontWeight: 600 }}>
                        {' · '}{f.uses} time{f.uses === 1 ? '' : 's'}
                      </small>
                    </b>
                  </div>
                ))}
                <span className="hint">
                  Counted on the server, inside the action itself — so this is what happened,
                  not what a browser reported. A feature missing from this list is one nobody
                  used, which is the more useful half of the page.
                </span>
              </>
            )}
          </Card>

          {/* Where the money stands.

              The headline is deliberately not revenue. At this size revenue
              is a rounding error and the number that decides whether anybody
              is still here next month is how many people are about to run out
              of free time without a card behind them. */}
          <Card className="tint">
            <SectionHead title="Standing" />
            <div className="stat-row">
              <Stat
                label="Covered"
                now={stats.billing.paid}
                foot={stats.billing.paying + ' paying, '
                  + (stats.billing.paid - stats.billing.paying) + ' on free months'}
              />
              <Stat
                label="On the free trial"
                now={stats.billing.onTrial}
                foot={stats.billing.browsing + ' run out and browsing'}
              />
            </div>
            <div className="row between">
              <span className="sub">
                Free time ending <b>within a week</b>, no card behind it
              </span>
              <b style={{ color: stats.billing.endingSoon ? 'var(--bergamot-deep)' : 'inherit' }}>
                {stats.billing.endingSoon}
              </b>
            </div>
            {stats.billing.cancelled > 0 && (
              <div className="row between">
                <span className="sub">Cancelled, still inside what they paid for</span>
                <b>{stats.billing.cancelled}</b>
              </div>
            )}
            <span className="hint">
              {stats.billing.endingSoon > 0
                ? 'These are the people to hand a promo code to, or to ask what is missing. '
                  + 'After the date passes they can still read everything — they just stop '
                  + 'being able to post, message or apply.'
                : 'Nobody runs out in the next week.'}
            </span>
            <CompMonths onDone={reload} />
          </Card>

          {/* The launch list. Unredeemed codes are stock, not failure -- what
              matters is whether the ones handed out came back. */}
          <Card>
            <SectionHead title="Promo codes" />
            <div className="stat-row">
              <Stat
                label="Redeemed"
                now={stats.promos.redeemed}
                foot={stats.promos.unused + ' still unused'}
              />
              <Stat
                label="Months given"
                now={stats.promos.monthsGiven}
                foot={stats.promos.minted + ' codes minted in all'}
              />
            </div>
            <MintCodes onDone={reload} />
            {stats.promos.minted === 0 ? (
              <span className="hint">
                None minted yet. Mint some above and hand them out.
              </span>
            ) : stats.promos.recent.length === 0 ? (
              <span className="hint">
                {stats.promos.minted} minted and none redeemed yet. A code nobody
                types is a code nobody was given.
              </span>
            ) : (
              <>
                <div className="hr" />
                {stats.promos.recent.map((r, i) => (
                  <div className="row between" key={r.code + i}>
                    <span className="sub">
                      <b>{r.name}</b> · {r.role === 'house' ? 'home' : 'cleaner'}
                      {r.note ? ' · ' + r.note : ''}
                    </span>
                    <span className="sub tight">{r.code} · {r.day}</span>
                  </div>
                ))}
              </>
            )}
          </Card>

          {/* Who left. Every account matters at this size, and a deletion
              used to leave no trace here at all. */}
          {stats.deletions && (
            <Card>
              <SectionHead title="Deleted accounts" />
              <div className="stat-row">
                <Stat label="Deleted" now={stats.deletions.total}
                  foot={stats.deletions.homes + ' homes · ' + stats.deletions.cleaners + ' cleaners'} />
                <Stat label="This week" now={stats.deletions.lastWeek} foot="in the last 7 days" />
              </div>
              {stats.deletions.total === 0 ? (
                <span className="hint">Nobody has deleted their account.</span>
              ) : (
                <>
                  <span className="sub tight">
                    Why: {stats.deletions.reasons.map((r) => (LEFT_BECAUSE[r.reason] ?? r.reason) + ' ' + r.n).join(' · ')}
                  </span>
                  <div className="hr" />
                  {stats.deletions.recent.map((d, i) => (
                    <div className="col" key={i} style={{ gap: 2, marginBottom: 6 }}>
                      <div className="row between">
                        <span className="sub">
                          <b>{d.role === 'house' ? 'A home' : 'A cleaner'}</b>{d.town ? ' in ' + d.town : ''}
                          {' · '}{LEFT_BECAUSE[d.reason ?? 'unsaid']}
                        </span>
                        <span className="sub tight">{d.left}</span>
                      </div>
                      <span className="sub tight">Joined {d.joined}, stayed {d.stayed}</span>
                      {d.note && <span className="sub">&quot;{d.note}&quot;</span>}
                    </div>
                  ))}
                </>
              )}
            </Card>
          )}

          {/* Sharing. One number says whether it works: how many people
              arrived because somebody else told them to. */}
          <Card>
            <SectionHead title="Sharing" />
            <div className="stat-row">
              <Stat
                label="Joined on a share code"
                now={stats.referrals.signups}
                foot={stats.referrals.sharers
                  + (stats.referrals.sharers === 1 ? ' person brought them' : ' people brought them')}
              />
              <Stat
                label="Months earned"
                now={stats.referrals.monthsEarned}
                foot={stats.referrals.atCap + ' at the ceiling'}
              />
            </div>
            {stats.referrals.top.length === 0 ? (
              <span className="hint">
                Nobody has brought anybody yet. Every account already has a code —
                it is in Settings, under Codes.
              </span>
            ) : (
              <>
                <div className="hr" />
                {stats.referrals.top.map((r, i) => (
                  <div className="row between" key={r.name + i}>
                    <span className="sub">
                      <b>{r.name}</b> · {r.role === 'house' ? 'home' : 'cleaner'}
                    </span>
                    <span className="sub tight">
                      {r.joined} joined · {r.months} {r.months === 1 ? 'month' : 'months'}
                    </span>
                  </div>
                ))}
                <span className="hint">
                  Each signup earns the sharer a month, up to six. The person joining
                  pays nothing and gets nothing — joining is the whole bar.
                </span>
              </>
            )}
          </Card>

          <SectionHead title="Calendar reports" />
          {stats.reports.length === 0 ? (
            <Card><span className="sub">None. Good.</span></Card>
          ) : (
            stats.reports.map((r, i) => (
              <Card key={i}>
                <div className="row between">
                  <b style={{ fontSize: 14.5 }}>{r.cleaner}</b>
                  <span className="sub tight">{ago(r.at)}</span>
                </div>
                <span className="sub">
                  {r.by} · about {r.date}
                </span>
                <div className="chips">
                  {r.reasons.map((x) => <span className="chip warm" key={x}>{x}</span>)}
                </div>
                {r.detail && <span className="sub">{r.detail}</span>}
              </Card>
            ))
          )}
          {stats.reports.length > 0 && (
            <span className="hint">
              One home reporting one cleaner means very little. The same cleaner reported by
              several unconnected homes is the only pattern worth acting on.
            </span>
          )}

          <SectionHead title="Sent through the app" />
          {stats.feedback.length === 0 ? (
            <Card><span className="sub">Nothing yet.</span></Card>
          ) : (
            stats.feedback.map((f, i) => (
              <Card key={i}>
                <div className="row between">
                  <b style={{ fontSize: 14.5 }}>{f.name}</b>
                  <span className="sub tight">{ago(f.at)}</span>
                </div>
                <span className="sub tight">
                  {f.role}{f.topic ? ' · ' + f.topic : ''}{f.screen ? ' · ' + f.screen : ''}
                </span>
                <span className="sub">{f.text}</span>
              </Card>
            ))
          )}
        </>
      )}
    </div>
  );
}

/** A count, with the same count from the stretch before it. */
/* ==========================================================================
   What this device is actually doing

   The bottom of the installed iPhone app has been "fixed" three times now,
   twice on a diagnosis made from this machine, and it is still wrong. The
   common factor in every wrong answer is that I cannot see an iPhone: the
   insets, the viewport units and the standalone flags all resolve to
   something else here, so every explanation was a guess that survived only
   because it could not be checked.

   This asks the device. Open it on the phone that has the problem and read
   it off. The line that matters most is the last one -- it names the element
   occupying the space below the tab bar, which settles what is taking up the
   room rather than inviting another theory about it.
   ========================================================================== */

function DeviceLayout() {
  const [rows, setRows] = useState<[string, string][]>([]);

  useEffect(() => {
    const read = () => {
      const px = (n: number) => Math.round(n) + 'px';
      const cs = getComputedStyle(document.documentElement);
      const q = (sel: string) => document.querySelector(sel);
      const rect = (sel: string) => {
        const el = q(sel);
        if (!el) return 'missing';
        const r = el.getBoundingClientRect();
        return px(r.top) + ' → ' + px(r.bottom) + '  (h ' + px(r.height) + ')';
      };

      /* 100dvh, resolved by the device rather than assumed. */
      const probe = document.createElement('div');
      probe.style.cssText = 'position:absolute;visibility:hidden;height:100dvh;top:0;left:0;width:1px';
      document.body.appendChild(probe);
      const dvh = probe.getBoundingClientRect().height;
      probe.remove();

      const bar = q('.tabbar');
      const barBottom = bar ? bar.getBoundingClientRect().bottom : 0;
      const labels = [...document.querySelectorAll('.tabbar .tab span')]
        .map((e) => e.getBoundingClientRect().bottom);

      /* Whatever is sitting in the space below the tab bar, named. */
      const below: string[] = [];
      for (const y of [barBottom + 4, window.innerHeight - 30, window.innerHeight - 6]) {
        if (y <= 0 || y > window.innerHeight) continue;
        const el = document.elementFromPoint(Math.round(window.innerWidth / 2), Math.round(y));
        below.push(px(y) + ': ' + (el ? (el.className || el.tagName).toString().slice(0, 34) : 'nothing'));
      }

      /* The one line worth reading.

         A translucent status bar -- what an app installed before September
         9th has -- gets a window 44pt shorter than the screen, anchored at
         the top, and those 44pt sit at the bottom OUTSIDE the page where no
         stylesheet can reach them. Deploying cannot change it, because iOS
         reads the setting once, when the app is added to the home screen.

         So rather than leaving that to be inferred from three numbers, it is
         worked out here and said in words. */
      const lost = window.screen.height - window.innerHeight;
      const translucent = lost > 2
        && Math.abs(lost - parseFloat(cs.getPropertyValue('--safe-top') || '0')) <= 2;
      const verdict = translucent
        ? px(lost) + ' of the screen is outside the app — REMOVE IT FROM THE HOME '
          + 'SCREEN AND ADD IT AGAIN'
        : lost > 2
          ? px(lost) + ' unaccounted for'
          : 'the app has the whole screen ✓';

      setRows([
        ['THE ANSWER', verdict],
        ['window.innerHeight', px(window.innerHeight)],
        ['visualViewport', window.visualViewport ? px(window.visualViewport.height) : 'none'],
        ['100dvh resolves to', px(dvh)],
        ['screen.height', px(window.screen.height) + ' @' + window.devicePixelRatio + 'x'],
        ['--safe-top', cs.getPropertyValue('--safe-top').trim() || '(empty)'],
        ['--safe-bottom', cs.getPropertyValue('--safe-bottom').trim() || '(empty)'],
        ['--app-height', cs.getPropertyValue('--app-height').trim() || '(unset — good)'],
        ['standalone (media)', String(matchMedia('(display-mode: standalone)').matches)],
        ['standalone (nav)', String((navigator as { standalone?: boolean }).standalone === true)],
        ['.stage', rect('.stage')],
        ['.phone', rect('.phone')],
        ['.tabbar', rect('.tabbar')],
        ['tabbar padding-bottom', bar ? getComputedStyle(bar).paddingBottom : 'missing'],
        ['lowest label bottom', labels.length ? px(Math.max(...labels)) : 'none'],
        ['gap below .phone', px(window.innerHeight - (q('.phone')?.getBoundingClientRect().bottom ?? 0))],
        ['top padding applied', getComputedStyle(q('.phone') ?? document.body).paddingTop],
        ['what is below the bar', below.join('   ')],
      ]);
    };
    read();
    const t = setInterval(read, 1500);
    return () => clearInterval(t);
  }, []);

  return (
    <Card>
      <SectionHead title="This device" />
      <span className="hint">
        Open this on the phone with the problem and send the picture. Every
        number here is read off the device, not guessed at from a laptop.
      </span>
      <div className="devrows">
        {rows.map(([k, v]) => (
          <div className="devrow" key={k}>
            <span>{k}</span>
            <b>{v}</b>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** A number, with the same number from the stretch before it.
 *
 *  `now` on its own is for a total rather than a count over a window: how
 *  many people are covered today has no "before", and saying "same as before"
 *  under it is not neutral -- it reads as a trend that was measured and
 *  found flat. It was not measured at all. */
const LEVEL_COLOUR: Record<string, string> = {
  ok: 'var(--good)',
  warn: 'var(--bergamot-deep)',
  bad: 'var(--bad)',
  unknown: 'var(--ink-2)',
};

const VERDICT_WORDS: Record<string, string> = {
  ok: 'Everything is fine',
  warn: 'Worth a look',
  bad: 'Something needs you',
  unknown: 'Could not check everything',
};

/** Everything, in one card.
 *
 *  Oster asked for "a check for everything" before two weeks away. The point
 *  of putting it here rather than leaving it at the endpoint is that this is
 *  the page he will already be opening on a phone -- a check nobody runs is
 *  not a check.
 *
 *  Anything not ok is listed with what to do about it; the rest collapses to
 *  a count, because a wall of green teaches you to stop reading. */
function HealthCard() {
  const [h, setH] = useState<Awaited<ReturnType<typeof api.health>> | null>(null);
  const [failed, setFailed] = useState('');
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = () => {
    setBusy(true);
    api.health()
      .then((r) => { setH(r); setFailed(''); })
      .catch((e) => setFailed(e instanceof ApiError ? e.message : 'Could not run the check.'))
      .finally(() => setBusy(false));
  };
  useEffect(load, []);

  if (failed) {
    return (
      <Card className="warm">
        <SectionHead title="Health" />
        <span className="sub">{failed}</span>
      </Card>
    );
  }
  if (!h) return <Card><span className="sub">Checking…</span></Card>;

  const trouble = h.checks.filter((c) => c.level !== 'ok');
  const shown = showAll ? h.checks : trouble;

  return (
    <Card className={h.verdict === 'ok' ? 'tint' : 'warm'}>
      <SectionHead title="Health" />
      <div className="row between">
        <b style={{ fontSize: 17, color: LEVEL_COLOUR[h.verdict] }}>
          {VERDICT_WORDS[h.verdict]}
        </b>
        <button type="button" className="btn quiet sm" disabled={busy} onClick={load}>
          {busy ? 'Checking…' : 'Check again'}
        </button>
      </div>

      {trouble.length === 0 && !showAll && (
        <span className="hint">
          All {h.checks.length} checks passed.
        </span>
      )}

      {shown.map((c) => (
        <div className="col" key={c.name} style={{ gap: 1, marginTop: 6 }}>
          <div className="row between">
            <span className="sub"><b>{c.name}</b></span>
            <span className="sub tight" style={{ color: LEVEL_COLOUR[c.level] }}>
              {c.level === 'ok' ? 'ok' : c.level === 'unknown' ? 'not checked' : c.level}
            </span>
          </div>
          <span className="hint">{c.detail}</span>
          {c.fix && (
            <span className="hint" style={{ color: LEVEL_COLOUR[c.level] }}>{c.fix}</span>
          )}
        </div>
      ))}

      <button
        type="button"
        className="btn quiet sm"
        style={{ alignSelf: 'flex-start', marginTop: 8 }}
        onClick={() => setShowAll((v) => !v)}
      >
        {showAll ? 'Only what needs attention' : 'Show all ' + h.checks.length + ' checks'}
      </button>
    </Card>
  );
}

/** Mint launch codes from here rather than from a terminal.
 *
 *  The codes are shown once, big, and stay on screen until the page is left:
 *  a list of codes that scrolls away before it is copied is a list of codes
 *  that has to be minted again. */
function MintCodes({ onDone }: { onDone: () => void }) {
  const [count, setCount] = useState(10);
  const [months, setMonths] = useState(2);
  const [note, setNote] = useState('');
  const [made, setMade] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  return (
    <>
      <div className="hr" />
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label className="col" style={{ gap: 3 }}>
          <span className="sub tight">How many</span>
          <input
            className="input" inputMode="numeric" style={{ width: 74 }}
            value={count} onChange={(e) => setCount(Number(e.target.value.replace(/\D/g, '')) || 0)}
          />
        </label>
        <label className="col" style={{ gap: 3 }}>
          <span className="sub tight">Months each</span>
          <input
            className="input" inputMode="numeric" style={{ width: 74 }}
            value={months} onChange={(e) => setMonths(Number(e.target.value.replace(/\D/g, '')) || 0)}
          />
        </label>
        <label className="col grow" style={{ gap: 3, minWidth: 120 }}>
          <span className="sub tight">Note (optional)</span>
          <input
            className="input" placeholder="launch list"
            value={note} maxLength={80} onChange={(e) => setNote(e.target.value)}
          />
        </label>
        <button
          type="button" className="btn"
          disabled={busy || count < 1 || months < 1}
          onClick={() => {
            setBusy(true);
            api.mintPromos({ count, months, note: note.trim(), role: null })
              .then((r) => { setMade(r.codes); onDone(); })
              .catch((e) => toast(e instanceof ApiError ? e.message : 'Could not mint those.'))
              .finally(() => setBusy(false));
          }}
        >
          {busy ? 'Minting…' : 'Mint codes'}
        </button>
      </div>

      {made.length > 0 && (
        <>
          <div
            className="sqlbox"
            style={{ userSelect: 'all', marginTop: 8, whiteSpace: 'pre-wrap' }}
          >
            {made.join('  ')}
          </div>
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button" className="btn quiet sm"
              onClick={() => {
                void navigator.clipboard.writeText(made.join('\n'))
                  .then(() => toast('Copied.'))
                  .catch(() => toast('Could not copy just now.'));
              }}
            >
              Copy them
            </button>
            <span className="hint">
              {made.length} codes, {months} {months === 1 ? 'month' : 'months'} each.
              Each works once.
            </span>
          </div>
        </>
      )}
    </>
  );
}

/** Give somebody months without a card, by phone number. */
function CompMonths({ onDone }: { onDone: () => void }) {
  const [phone, setPhone] = useState('');
  const [months, setMonths] = useState(1);
  const [busy, setBusy] = useState(false);

  return (
    <>
      <div className="hr" />
      <span className="sub tight">Give somebody free months</span>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <input
          className="input grow" style={{ minWidth: 140 }} inputMode="tel"
          placeholder="(631) 555-0123" value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <input
          className="input" inputMode="numeric" style={{ width: 70 }}
          value={months} onChange={(e) => setMonths(Number(e.target.value.replace(/\D/g, '')) || 0)}
        />
        <button
          type="button" className="btn quiet"
          disabled={busy || phone.replace(/\D/g, '').length < 10 || months < 1}
          onClick={() => {
            setBusy(true);
            api.compMonths({ phone, months })
              .then((r) => { toast(r.message); setPhone(''); onDone(); })
              .catch((e) => toast(e instanceof ApiError ? e.message : 'Could not do that.'))
              .finally(() => setBusy(false));
          }}
        >
          {busy ? 'Working…' : 'Give'}
        </button>
      </div>
      <span className="hint">
        Lands after whatever they already have, so a trial is not overwritten.
      </span>
    </>
  );
}

function Stat(
  { label, pair, now, foot }:
  { label: string; pair?: Pair; now?: number; foot?: string },
) {
  const value = pair ? pair.now : (now ?? 0);
  const change = pair ? pair.now - pair.before : 0;
  return (
    <div className="col grow">
      <span className="sub tight">{label}</span>
      <b style={{ fontSize: 26, lineHeight: 1.1 }}>{value}</b>
      {pair && (
        <span className="sub tight">
          {pair.before === 0 && pair.now === 0
            ? 'none before either'
            : change === 0
              ? 'same as before'
              : (change > 0 ? '+' : '') + change + ' on the stretch before'}
        </span>
      )}
      {foot && <span className="hint">{foot}</span>}
    </div>
  );
}

/* ------------------------------------------------------ website visitors */

/** People who opened the site: counted without cookies, one per day each,
    members apart (they are not new people). The last fourteen days as bars,
    then the last seven by where they came from and where they landed. */
function WebsiteVisitors() {
  const [r, setR] = useState<VisitorReport | null>(null);
  useEffect(() => { api.visitors().then(setR).catch(() => setR(null)); }, []);
  if (!r) return null;
  const top = Math.max(1, ...r.days.map((d) => d.visitors));
  const pct = (n: number, of: number) => (of ? Math.round((n / of) * 100) + '%' : '—');
  const weekday = (iso: string) => new Date(iso + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'narrow', timeZone: 'UTC' });
  const Table = ({ rows, label }: { rows: VisitorReport['sources']; label: string }) => (
    <div className="col" style={{ gap: 6 }}>
      <div className="row between">
        <span className="sub tight" style={{ fontWeight: 700 }}>{label}</span>
        <span className="sub tight">visitors · signed up</span>
      </div>
      {rows.length === 0 && <span className="hint">Nobody yet.</span>}
      {rows.map((x) => (
        <div className="row between" key={x.name}>
          <span className="sub" style={{ overflowWrap: 'anywhere' }}>{x.name}</span>
          <b style={{ fontVariantNumeric: 'tabular-nums' }}>{x.visitors} · {x.signups}</b>
        </div>
      ))}
    </div>
  );
  return (
    <Card>
      <SectionHead title="Website visitors" />
      <div className="stat-row">
        <Stat label="Today" now={r.today.visitors} foot={r.today.signups + ' signed up'} />
        <Stat label="Last 7 days" now={r.week.visitors} foot={r.week.signups + ' signed up · ' + pct(r.week.signups, r.week.visitors)} />
      </div>
      <div className="visitbars" role="img" aria-label="Visitors per day, last 14 days">
        {r.days.map((d) => (
          <div className="visitbar" key={d.day} title={d.day + ': ' + d.visitors + ' visitors, ' + d.signups + ' signed up'}>
            <span className="n">{d.visitors || ''}</span>
            <div className="bar" style={{ height: Math.round((d.visitors / top) * 64) + 'px' }}>
              {d.signups > 0 && <div className="joined" style={{ height: Math.max(3, Math.round((d.signups / top) * 64)) + 'px' }} />}
            </div>
            <span className="d">{weekday(d.day)}</span>
          </div>
        ))}
      </div>
      <Table rows={r.sources} label="Where they came from (7 days)" />
      <Table rows={r.landings} label="Where they landed (7 days)" />
      <span className="hint">
        People who opened ozeret.app and were not signed in, each counted once a day. No cookies:
        link previews and search crawlers are left out. &quot;direct&quot; is anyone with no
        referring site — a typed address, a QR code, or a link tapped inside WhatsApp. Add
        ?src=something to a link you hand out (ozeret.app/?src=card) and it shows up here by name.
        Signed-in members today: {r.today.members}.
      </span>
    </Card>
  );
}
